import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@cere/cere-design-system';
import AddIcon from '@mui/icons-material/Add';
import type { Scope } from '@cef-ai/vault-sdk';
import { useVaultSession } from '@/src/state/session';
import { displayAgentName } from '@/lib/vault/agent-display';
import { ScopeCard, type ScopeChipAgent } from './ScopeCard';
import { CreateScopeDialog } from './CreateScopeDialog';
import { EditScopeDialog } from './EditScopeDialog';
import { DeleteScopeDialog } from './DeleteScopeDialog';
import '@/src/styles/scopes.css';

export function ScopesPage() {
  const session = useVaultSession();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Scope | null>(null);
  const [deleting, setDeleting] = useState<Scope | null>(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get('create') === '1') {
      setCreateOpen(true);
      const next = new URLSearchParams(params);
      next.delete('create');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const agentsByScope = useMemo(() => {
    const map: Record<string, ScopeChipAgent[]> = {};
    for (const a of session.agents) {
      (map[a.scope] ??= []).push({
        agentId: a.agentId,
        name: session.agentCards[a.agentId]?.name ?? displayAgentName(a.agentId),
      });
    }
    return map;
  }, [session.agents, session.agentCards]);

  const scopeCount = session.scopes.length;
  const agentTotal = session.agents.length;

  return (
    <div className="vault-scopes-page">
      <div className="vault-scopes-page__head">
        <div className="vault-scopes-page__head-text">
          <div className="vault-scopes-page__eyebrow">
            {scopeCount} {scopeCount === 1 ? 'scope' : 'scopes'} · {agentTotal} {agentTotal === 1 ? 'agent' : 'agents'} attached
          </div>
          <h2>Scopes</h2>
          <p className="vault-scopes-page__sub">
            Group agents and control what each can write to. The display name is editable; the underlying name is permanent.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreateOpen(true)} startIcon={<AddIcon />}>
          Create scope
        </Button>
      </div>

      {scopeCount === 0 ? (
        <div className="vault-scopes__empty">No scopes yet. Create one to group your agents.</div>
      ) : (
        <div className="vault-scopes__grid">
          {session.scopes.map((s) => (
            <ScopeCard
              key={s.name}
              scope={s}
              agents={agentsByScope[s.name] ?? []}
              onEdit={() => setEditing(s)}
              onDelete={() => setDeleting(s)}
            />
          ))}
        </div>
      )}

      <CreateScopeDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {editing && <EditScopeDialog open scope={editing} onClose={() => setEditing(null)} />}
      {deleting && (
        <DeleteScopeDialog
          open
          scope={deleting}
          agentCount={(agentsByScope[deleting.name] ?? []).length}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
